import {
  connectorChain,
  type ConnectorEntity,
  connectorEntity,
  entityNoun,
  entitySubject,
} from '../../_shared/domain/session.domain';
import type {
  SessionDetail,
  SessionStepDetail,
  TargetResult,
  TargetStepResult,
} from './session-detail.model';

/**
 * The session detail read as what it actually is: a ledger of (step, target)
 * outcomes.
 *
 * Everything here is derived from `profile_results[].steps` (the
 * `enrichment_step_results` rows) rather than from the steps' own job counters.
 * The two can disagree (a redelivered job bumps a counter twice; `total_jobs` can
 * be smaller than the target set), and the page must not show a card that
 * contradicts the table underneath it. The one thing taken from the step row is
 * its `status`: the lifecycle of a step is the step's own business, not the
 * ledger's.
 *
 * Pure: no React/Redux/fetch.
 */

/** What one (step, target) cell says. `not_run` is the absence of a ledger row. */
export type StepOutcome = 'enriched' | 'skipped' | 'error' | 'not_run';

/** Ledger outcome → the word the UI uses. A missing row is `not_run`, not an error. */
export function stepOutcome(entry: TargetStepResult | undefined): StepOutcome {
  switch (entry?.outcome) {
    case 'SUCCESS':
      return 'enriched';
    case 'SKIPPED':
      return 'skipped';
    case 'ERROR':
      return 'error';
    default:
      return 'not_run';
  }
}

/** A target's entry for one step, or undefined when the step is not in its list. */
export function targetStepEntry(
  target: TargetResult,
  stepId: string,
): TargetStepResult | undefined {
  return target.steps.find((s) => s.step_id === stepId);
}

/** How many targets landed on each outcome. The four always sum to the target count. */
export interface StepCounters {
  enriched: number;
  skipped: number;
  errors: number;
  notRun: number;
}

/** One card of the Pipeline section. */
export interface PipelineStepView {
  id: string;
  /** 1-based, the way the cards are labelled ("STEP 1"). `position` is 0-based. */
  index: number;
  /** Display name, e.g. "Profiles". */
  label: string;
  /** Raw connector code, e.g. `linkedin_standard`. */
  connector: string;
  /** The step's own lifecycle status (PENDING / IN_PROGRESS / COMPLETED / …). */
  status: string;
  /** The config spelled out, or "no configuration" when it is empty. */
  configLabel: string;
  /**
   * 1-based index of the first earlier step running the same connector with the
   * same config, or null. Non-null means nothing but the position tells the two
   * apart; worth saying out loud, since the session title cannot.
   */
  identicalTo: number | null;
  counters: StepCounters;
  /** Targets with a terminal outcome recorded, out of the whole target set. */
  recorded: number;
  total: number;
  /**
   * The step's own job counters, and only when the ledger holds nothing for it.
   * Null for everything a current run produces; see `unledgeredJobs`.
   */
  jobsWithoutLedger: StepJobCounters | null;
  startedAt: string | null;
  completedAt: string | null;
}

/**
 * What one step is pointed at; its own, not the session's.
 *
 * A card names its own count, and in a `Profiles → Companies` chain the two
 * cards do not name the same thing. Read off the step's resolved label, the way
 * every other entity question is answered on this side.
 */
export function stepEntity(step: PipelineStepView): ConnectorEntity {
  return connectorEntity(step.label);
}

/**
 * A step's own job counters; the other, older tally.
 *
 * `enrichment_steps` keeps running totals of its own, and they are not
 * idempotent: a redelivered pg-boss job bumps one twice. That is precisely why
 * every figure on this page is derived from the ledger instead. They are read
 * here for one case, and always attributed out loud to their source rather than
 * stated as fact.
 */
export interface StepJobCounters {
  completed: number;
  failed: number;
  skipped: number;
  total: number;
}

/**
 * The step's own counters, kept only when the ledger has no row for it at all
 * and they claim work was done.
 *
 * A run from before the ledger existed (its first row was written on
 * 2026-07-28) leaves a step carrying two completed and two failed jobs and not
 * one per-target outcome. Read through the ledger alone, that step's card said
 * nothing had been attempted while its own status pill said "Partially failed":
 * one card contradicting itself, and the counter's tooltip going as far as
 * "these profiles never went through", which was simply untrue. Two of them had
 * been through and succeeded.
 *
 * Only total absence counts. A ledger that is merely *behind* the counters
 * proves nothing (the counters over-report, which is the whole reason they were
 * abandoned) so the partial case is left alone rather than guessed at.
 *
 * Null in every ordinary case, a step that has not started included: there the
 * two sources agree that nothing has happened yet. Null too while the step is
 * still going; an empty ledger under a running step is the normal state of a
 * step that has not finished anything, and the loader already says so.
 */
export function unledgeredJobs(step: SessionStepDetail, recorded: number): StepJobCounters | null {
  if (recorded > 0) return null;
  if (pendingSense(step.status) === 'queued') return null;
  const jobs: StepJobCounters = {
    completed: step.completed_jobs,
    failed: step.failed_jobs,
    skipped: step.skipped_jobs,
    total: step.total_jobs,
  };
  const attempted = jobs.completed + jobs.failed + jobs.skipped;
  return attempted > 0 ? jobs : null;
}

/**
 * What those counters say, as a clause the card can attribute: "2 enriched,
 * 2 errors out of 4 targets". Zeroes are dropped; this is a claim being
 * quoted, not the four-outcome tally the ledger draws, where a zero is an answer.
 */
export function jobsSentence(jobs: StepJobCounters, entity: ConnectorEntity): string {
  const parts: [number, string][] = [
    [jobs.completed, 'enriched'],
    [jobs.skipped, 'skipped'],
    [jobs.failed, plural(jobs.failed, 'error', 'errors')],
  ];
  const said = parts.filter(([n]) => n > 0).map(([n, word]) => `${n} ${word}`);
  if (said.length === 0) return `nothing out of ${jobs.total}`;
  return `${said.join(', ')} out of ${jobs.total} ${entityNoun(jobs.total, entity)}`;
}

const EMPTY_COUNTERS: StepCounters = { enriched: 0, skipped: 0, errors: 0, notRun: 0 };

/** Tally one step's column of the ledger across every target. */
export function stepCounters(targets: TargetResult[], stepId: string): StepCounters {
  return targets.reduce<StepCounters>(
    (acc, target) => {
      switch (stepOutcome(targetStepEntry(target, stepId))) {
        case 'enriched':
          return { ...acc, enriched: acc.enriched + 1 };
        case 'skipped':
          return { ...acc, skipped: acc.skipped + 1 };
        case 'error':
          return { ...acc, errors: acc.errors + 1 };
        default:
          return { ...acc, notRun: acc.notRun + 1 };
      }
    },
    { ...EMPTY_COUNTERS },
  );
}

/**
 * The config as one line. Key order follows the object's own, so two steps built
 * the same way read the same way.
 */
export function configLabel(config: Record<string, unknown>): string {
  const entries = Object.entries(config);
  if (entries.length === 0) return 'no configuration';
  return entries.map(([key, value]) => `${key}: ${JSON.stringify(value)}`).join(' · ');
}

/** Two steps are indistinguishable when connector and config both match. */
function sameStep(a: SessionStepDetail, b: SessionStepDetail): boolean {
  return a.connector === b.connector && JSON.stringify(a.config) === JSON.stringify(b.config);
}

/**
 * One card per step, ordered by position.
 *
 * Step names come from the session's own `type` string; the backend already
 * formatted and joined the connector labels there, so they are read back out
 * rather than re-derived. A step past the end of that string falls back to its
 * raw connector code.
 */
export function buildPipelineSteps(session: SessionDetail): PipelineStepView[] {
  const ordered = [...session.steps].sort((a, b) => a.position - b.position);
  const labels = connectorChain(session.type);
  const total = session.profile_results.length;

  return ordered.map((step, i) => {
    const counters = stepCounters(session.profile_results, step.id);
    const recorded = total - counters.notRun;

    return {
      id: step.id,
      index: i + 1,
      label: labels[i] ?? step.connector,
      connector: step.connector,
      status: step.status,
      configLabel: configLabel(step.config),
      identicalTo: (() => {
        const twin = ordered.findIndex((other) => sameStep(other, step));
        return twin >= 0 && twin < i ? twin + 1 : null;
      })(),
      counters,
      recorded,
      total,
      jobsWithoutLedger: unledgeredJobs(step, recorded),
      startedAt: step.started_at,
      completedAt: step.completed_at,
    };
  });
}

/**
 * The session totals, counted in step results rather than in targets.
 *
 * This is the distinction the old header got wrong: it put "4 targets" next to
 * "3 enriched" without saying that the two count different things. A four-target,
 * two-step session holds eight step results, and enriched/skipped/errors are
 * shares of those eight.
 */
export interface LedgerTotals extends StepCounters {
  /** Terminal outcomes on record. */
  recorded: number;
  /** Outcomes there would be once every target has been through every step. */
  expected: number;
  targets: number;
  steps: number;
}

export function ledgerTotals(session: SessionDetail): LedgerTotals {
  const targets = session.profile_results.length;
  const steps = session.steps.length;
  const counters = session.steps.reduce<StepCounters>((acc, step) => {
    const c = stepCounters(session.profile_results, step.id);
    return {
      enriched: acc.enriched + c.enriched,
      skipped: acc.skipped + c.skipped,
      errors: acc.errors + c.errors,
      notRun: acc.notRun + c.notRun,
    };
  }, EMPTY_COUNTERS);

  return {
    ...counters,
    recorded: counters.enriched + counters.skipped + counters.errors,
    expected: targets * steps,
    targets,
    steps,
  };
}

// ── What the unrecorded share of a bar means ──

/**
 * The bar only ever paints outcomes on record, so before the first one there is
 * nothing to paint; and a run launched ten seconds ago draws exactly the same
 * empty rail as a run that died two days ago. The counters cannot tell the two
 * apart; only the status can.
 *
 * `queued`: the run can still reach these targets. A queue, not a failure.
 * `not_run`: it cannot, and never will. A hole in the ledger.
 */
export type PendingSense = 'queued' | 'not_run';

/** Statuses from which unrecorded outcomes are still expected to arrive. */
const UNFINISHED_STATUSES = ['PENDING', 'IN_PROGRESS', 'PAUSED'] as const;

export function pendingSense(status: string): PendingSense {
  const unfinished: readonly string[] = UNFINISHED_STATUSES;
  return unfinished.includes(status) ? 'queued' : 'not_run';
}

/**
 * A step's own reading, which its status alone gets wrong.
 *
 * A step left `PENDING` inside a run that was cancelled keeps that status
 * forever, and nothing is coming for it: the run decides first, the step only
 * refines. The other way round matters too; a `COMPLETED` step inside a running
 * session has closed its books, so what it never recorded it never will.
 */
export function stepPendingSense(sessionStatus: string, stepStatus: string): PendingSense {
  if (pendingSense(sessionStatus) === 'not_run') return 'not_run';
  return pendingSense(stepStatus);
}

/**
 * Whether work is happening right now; the one thing on the page allowed to move.
 *
 * `PAUSED` is deliberately excluded: its targets are still queued, so they keep
 * the striped surface, but nothing is processing them. A bar that keeps marching
 * through a pause is a lie told by an animation.
 */
export function isWorking(status: string): boolean {
  return status === 'IN_PROGRESS';
}

/** The step carrying the run forward: both the run and the step are live. */
export function isStepWorking(sessionStatus: string, stepStatus: string): boolean {
  return isWorking(sessionStatus) && isWorking(stepStatus);
}

/**
 * The status a step may honestly put on its pill.
 *
 * A step cannot be more alive than the run it belongs to. Pausing a session
 * leaves its current step `IN_PROGRESS` in the database (nothing rewrites the
 * step row) so the page drew "● Paused" beside the title and "● In progress"
 * on the card two hundred pixels below, about the same run, at the same moment.
 * One of the two had to be wrong, and it was the card: nothing was processing.
 *
 * So a step claiming to work inside a run that is not working reports the run's
 * status instead. Every other combination is left alone: a `PENDING` step in a
 * cancelled run is genuinely pending and never claimed otherwise, and a step
 * that finished keeps its own outcome; a `COMPLETED` step inside a partially
 * failed run is exactly the fact the cards exist to show.
 */
export function stepDisplayStatus(sessionStatus: string, stepStatus: string): string {
  if (isWorking(stepStatus) && !isWorking(sessionStatus)) return sessionStatus;
  return stepStatus;
}

/** The last error a target holds, and which step it belongs to. */
export interface TargetError {
  reason: string;
  /** 1-based step index, so it reads the way the cards are labelled. */
  stepIndex: number;
  recordedAt: string | null;
}

/**
 * The failing step of a target, latest first.
 *
 * "Latest" is by step position, not by timestamp: the ledger records the outcome
 * of a step, and a later step's failure is the one that stopped the target. A
 * target that failed early and succeeded later still shows its error; the row's
 * per-step chips say the rest.
 */
export function lastError(target: TargetResult): TargetError | null {
  const failed = [...target.steps]
    .filter((s) => s.outcome === 'ERROR')
    .sort((a, b) => b.position - a.position)[0];
  if (!failed) return null;
  return {
    reason: failed.reason ?? 'No reason recorded',
    stepIndex: failed.position + 1,
    recordedAt: failed.created_at,
  };
}

/** Targets holding at least one ERROR in the ledger. */
export function targetsWithErrors(targets: TargetResult[]): TargetResult[] {
  return targets.filter((t) => t.steps.some((s) => s.outcome === 'ERROR'));
}

/**
 * Targets the run did not finish; those without a SUCCESS/SKIPPED outcome at
 * every step. Errors and never-processed alike.
 *
 * This is what "Retry all targets" actually re-enqueues since #460: the backend
 * computes the same set from the ledger and refuses the action when it is empty.
 * The label and the effect sentence are built from it, so the menu item states
 * the size of what it is about to do rather than the size of the batch.
 *
 * A target with no step entry at all counts as unfinished; a session with no
 * steps has nothing on record, and nothing on record is not a success.
 */
export function unfinishedTargets(targets: TargetResult[]): TargetResult[] {
  return targets.filter(
    (t) =>
      t.steps.length === 0 ||
      t.steps.some((s) => s.outcome !== 'SUCCESS' && s.outcome !== 'SKIPPED'),
  );
}

/**
 * Where a retry of these targets restarts, and what it costs.
 *
 * Mirrors `buildRetryPlan` on the backend, which is the only thing that decides:
 * a target resumes at the first step where it holds no SUCCESS/SKIPPED outcome,
 * the chain restarts at the earliest such step across the batch, and every ledger
 * row from there on (INCLUDING later successes) is dropped so the steps re-run.
 * That last part is the surprise worth stating before the click.
 */
export interface RetryEffect {
  /** 1-based step the chain restarts from. */
  resumeFrom: number;
  /** Terminal outcomes that will be discarded. */
  discarded: number;
  /** Of those, the ones that had succeeded or been skipped. */
  discardedSuccesses: number;
}

export function retryEffect(
  targets: TargetResult[],
  stepCount: number,
): RetryEffect | null {
  if (targets.length === 0 || stepCount === 0) return null;

  // A target that succeeded everywhere has no failing step and is re-run end to
  // end: the backend's own rule, not a default chosen here.
  const failFrom = targets.map((target) => {
    const ordered = [...target.steps].sort((a, b) => a.position - b.position);
    const firstMissing = ordered.find((s) => s.outcome !== 'SUCCESS' && s.outcome !== 'SKIPPED');
    return firstMissing ? firstMissing.position : 0;
  });

  const resumeFrom = Math.min(...failFrom);

  const dropped = targets.flatMap((target, i) =>
    target.steps.filter((s) => s.position >= failFrom[i] && s.outcome !== null),
  );

  return {
    resumeFrom: resumeFrom + 1,
    discarded: dropped.length,
    discardedSuccesses: dropped.filter((s) => s.outcome === 'SUCCESS' || s.outcome === 'SKIPPED')
      .length,
  };
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * A retry's effect as one sentence, for the button that is about to cause it.
 *
 * Says the two things a retry surprises people with: where the chain restarts,
 * which is not necessarily where you clicked, and that outcomes already on record
 * past that point are thrown away, successes included.
 */
export function retrySentence(subject: string, effect: RetryEffect | null): string {
  if (!effect) return 'Nothing to re-run.';

  const opening = `Re-runs ${subject} from step ${effect.resumeFrom}.`;
  if (effect.discarded === 0) return opening;

  const outcomes = `${effect.discarded} recorded ${plural(effect.discarded, 'outcome is', 'outcomes are')} discarded`;
  const successes =
    effect.discardedSuccesses > 0
      ? `, ${effect.discardedSuccesses} of them already successful`
      : '';
  return `${opening} ${outcomes}${successes}.`;
}

/**
 * What a reset costs, in one sentence.
 *
 * `resetPipeline` does more than clear the ledger: it also nulls `data_updated_at`
 * on every target, so profiles the run enriched go back to counting as never
 * enriched anywhere in the product. That reaches past this session, and the menu
 * item says so before the click rather than after.
 *
 * The clearing clause is dropped when there is nothing on record; "Clears 0
 * recorded outcomes" announces a defect rather than an effect, and a run older
 * than the ledger is in exactly that state. `retrySentence` already omits its own
 * count for the same reason.
 */
export function resetSentence(totals: LedgerTotals, entity: ConnectorEntity): string {
  const subject = `${entitySubject(totals.targets, entity)} as never enriched.`;
  const tail = `every step back to pending, and marks ${subject} Cannot be undone.`;

  if (totals.recorded === 0) return `Puts ${tail}`;

  const outcomes = `${totals.recorded} recorded ${plural(totals.recorded, 'outcome', 'outcomes')}`;
  return `Clears ${outcomes}, puts ${tail}`;
}

/**
 * "1 profile" / "3 failed companies": the subject the retry sentences take.
 *
 * Named for what the run is pointed at rather than for the code's word for it
 * (#530); `entitySubject` in `_shared` is where that naming lives, so the table,
 * the cards and these sentences cannot drift apart.
 */
export function targetsSubject(
  count: number,
  entity: ConnectorEntity,
  qualifier = '',
): string {
  return entitySubject(count, entity, qualifier);
}
